import { Component } from '@theme/component';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * Full-screen product image viewer (TDM, 2026-09-30), modelled on Carré Blanc's:
 * white overlay, one image at a time, + / − / reset pill, thumbnail strip, drag to
 * pan, wheel and pinch zoom, double-tap, swipe and arrow-key navigation.
 *
 * The dialog is an empty shell in the HTML. Its slides are built on open from the
 * gallery's own images, so the page no longer ships a second copy of every image
 * and the viewer only ever lists the images the colour filter left visible.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} stage
 * @property {HTMLElement} thumbnails
 * @property {HTMLElement} [counter]
 *
 * @typedef {{ source: HTMLElement, image: HTMLImageElement }} ZoomItem
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'stage', 'thumbnails'];

  static MAX_SCALE = 4;
  static STEP = 1.6;
  static CLICK_SCALE = 2.5;

  /** @type {ZoomItem[]} */
  #items = [];
  /** @type {HTMLElement[]} */
  #slides = [];
  #active = 0;
  #scale = 1;
  #x = 0;
  #y = 0;
  /** @type {Map<number, {x: number, y: number}>} */
  #pointers = new Map();
  /** @type {{x: number, y: number, tx: number, ty: number, dist: number, scale: number, moved: boolean} | null} */
  #gesture = null;
  #lastTap = 0;
  #frame = 0;
  /** @type {AbortController | null} */
  #openController = null;
  /** @type {Promise<void> | null} */
  #stylesheet = null;

  /**
   * Opens the viewer on the image the shopper clicked.
   * @param {number} index - Index of the clicked slide among all gallery slides.
   * @param {Event} event
   */
  async open(index, event) {
    event?.preventDefault?.();
    const gallery = this.closest('media-gallery');
    if (!gallery || this.refs.dialog.open) return;

    const sources = /** @type {HTMLElement[]} */ (
      Array.from(
        gallery.querySelectorAll(
          gallery.getAttribute('data-presentation') === 'grid' ? '.media-gallery__grid > li' : 'slideshow-slide'
        )
      )
    );
    this.#items = sources
      .filter((source) => !source.hidden && !source.classList.contains('variant-media-hidden'))
      .map((source) => ({ source, image: source.querySelector('img.product-media__image') }))
      .filter((item) => item.image instanceof HTMLImageElement);
    if (!this.#items.length) return;

    const target = event?.target instanceof Element ? event.target.closest('slideshow-slide, li') : sources[index];
    const start = Math.max(0, this.#items.findIndex((item) => item.source === target));

    await this.#loadStylesheet();
    this.#build();
    this.#bind();

    const { dialog } = this.refs;
    dialog.showModal();
    // showModal focuses the first button (the close cross) and draws its focus
    // ring; start on the image instead — arrows, +/− and Esc still work from there.
    this.refs.stage.focus({ preventScroll: true });
    document.documentElement.setAttribute('scroll-lock', '');
    this.#go(start, { instant: true });
    requestAnimationFrame(() => dialog.classList.add('is-open'));
  }

  close() {
    const { dialog } = this.refs;
    if (!dialog.open) return;
    const source = this.#items[this.#active]?.source;
    dialog.classList.remove('is-open');
    this.#openController?.abort();
    this.#openController = null;
    dialog.close();
    if (!document.querySelector('dialog[scroll-lock][open]')) {
      document.documentElement.removeAttribute('scroll-lock');
    }
    // Keep the page gallery on the image that was being viewed.
    const visibleSlides = this.#items.map((item) => item.source);
    const slideIndex = source ? visibleSlides.indexOf(source) : -1;
    if (slideIndex >= 0) this.dispatchEvent(new ZoomMediaSelectedEvent(slideIndex));
    window.dispatchEvent(new DialogCloseEvent());
  }

  /** Loads the viewer's stylesheet once, before the first open. */
  #loadStylesheet() {
    const href = this.dataset.stylesheet;
    if (!href || this.#stylesheet) return this.#stylesheet || Promise.resolve();
    this.#stylesheet = new Promise((resolve) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.onload = () => resolve();
      link.onerror = () => resolve();
      document.head.appendChild(link);
    });
    return this.#stylesheet;
  }

  #build() {
    const { stage, thumbnails, dialog } = this.refs;
    stage.replaceChildren();
    thumbnails.replaceChildren();
    this.#slides = this.#items.map(({ image }, i) => {
      const slide = document.createElement('div');
      slide.className = 'tdm-zoom__slide';
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'image');
      slide.setAttribute('aria-label', `${i + 1} / ${this.#items.length}`);
      const img = document.createElement('img');
      img.className = 'tdm-zoom__image';
      img.alt = image.alt;
      img.draggable = false;
      img.decoding = 'async';
      slide.appendChild(img);
      stage.appendChild(slide);

      const thumb = document.createElement('button');
      thumb.type = 'button';
      thumb.className = 'tdm-zoom__thumb';
      thumb.setAttribute('aria-label', image.alt ? `Voir : ${image.alt}` : `Image ${i + 1}`);
      const small = document.createElement('img');
      small.alt = '';
      small.loading = 'lazy';
      small.src = resize(image.currentSrc || image.src, 160);
      thumb.appendChild(small);
      thumb.addEventListener('click', () => this.#go(i));
      thumbnails.appendChild(thumb);
      return slide;
    });
    dialog.toggleAttribute('data-single', this.#items.length < 2);
  }

  #bind() {
    this.#openController?.abort();
    this.#openController = new AbortController();
    const { signal } = this.#openController;
    const { dialog, stage } = this.refs;

    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    }, { signal });
    dialog.addEventListener('keydown', this.#onKeyDown, { signal });
    dialog.addEventListener('click', this.#onAction, { signal });
    stage.addEventListener('pointerdown', this.#onPointerDown, { signal });
    stage.addEventListener('pointermove', this.#onPointerMove, { signal });
    stage.addEventListener('pointerup', this.#onPointerUp, { signal });
    stage.addEventListener('pointercancel', this.#onPointerUp, { signal });
    stage.addEventListener('wheel', this.#onWheel, { signal, passive: false });
    window.addEventListener('resize', () => this.#apply(false), { signal });
  }

  /** @param {MouseEvent} event */
  #onAction = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-zoom-action]') : null;
    if (!button) return;
    switch (button.getAttribute('data-zoom-action')) {
      case 'close': return this.close();
      case 'prev': return this.#go(this.#active - 1);
      case 'next': return this.#go(this.#active + 1);
      case 'in': return this.#zoomTo(this.#scale * ZoomDialog.STEP);
      case 'out': return this.#zoomTo(this.#scale / ZoomDialog.STEP);
      case 'reset': return this.#zoomTo(1);
    }
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key === 'ArrowRight') this.#go(this.#active + 1);
    else if (event.key === 'ArrowLeft') this.#go(this.#active - 1);
    else if (event.key === '+' || event.key === '=') this.#zoomTo(this.#scale * ZoomDialog.STEP);
    else if (event.key === '-') this.#zoomTo(this.#scale / ZoomDialog.STEP);
    else if (event.key === '0') this.#zoomTo(1);
    else return;
    event.preventDefault();
  };

  /**
   * Shows image `index` (wrapping), preloading its neighbours.
   * @param {number} index
   * @param {{instant?: boolean}} [options]
   */
  #go(index, { instant = false } = {}) {
    const count = this.#items.length;
    if (!count) return;
    this.#active = ((index % count) + count) % count;
    this.#scale = 1;
    this.#x = 0;
    this.#y = 0;

    for (const offset of [0, 1, -1]) this.#load((this.#active + offset + count) % count);

    this.#slides.forEach((slide, i) => {
      slide.classList.toggle('is-active', i === this.#active);
      slide.classList.toggle('is-instant', instant);
      slide.setAttribute('aria-hidden', String(i !== this.#active));
      const img = slide.firstElementChild;
      if (img instanceof HTMLElement) img.style.transform = '';
    });
    const strip = this.refs.thumbnails;
    const thumbs = /** @type {HTMLElement[]} */ (Array.from(strip.children));
    thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === this.#active)));
    // Scroll only the strip (scrollIntoView could also move the page behind the dialog).
    const current = thumbs[this.#active];
    if (current) {
      strip.scrollTo({
        left: current.offsetLeft - (strip.clientWidth - current.offsetWidth) / 2,
        behavior: instant ? 'instant' : 'smooth',
      });
    }
    if (this.refs.counter) this.refs.counter.textContent = `${this.#active + 1} / ${count}`;
    this.#apply(false);
  }

  /**
   * Gives a slide its screen-sized image (srcset) the first time it is needed.
   * @param {number} i
   */
  #load(i) {
    const img = /** @type {HTMLImageElement | null} */ (this.#slides[i]?.firstElementChild ?? null);
    const source = this.#items[i]?.image;
    if (!img || !source || img.getAttribute('src')) return;
    if (source.srcset) {
      img.sizes = '100vw';
      img.srcset = source.srcset;
    }
    img.src = source.currentSrc || source.src;
  }

  /** Swaps in the full-resolution file once the shopper actually zooms. */
  #loadFullResolution() {
    const img = /** @type {HTMLImageElement | null} */ (this.#slides[this.#active]?.firstElementChild ?? null);
    const full = this.#items[this.#active]?.image.getAttribute('data_max_resolution');
    if (!img || !full || img.dataset.full) return;
    img.dataset.full = 'true';
    const hi = new Image();
    hi.decoding = 'async';
    hi.onload = () => {
      img.removeAttribute('srcset');
      img.src = full;
    };
    hi.src = full;
  }

  /** @returns {{w: number, h: number}} size of the image as displayed at scale 1 (object-fit: contain) */
  #baseSize() {
    const { stage } = this.refs;
    const img = /** @type {HTMLImageElement | null} */ (this.#slides[this.#active]?.firstElementChild ?? null);
    const sw = stage.clientWidth;
    const sh = stage.clientHeight;
    const ratio = img && img.naturalWidth ? img.naturalWidth / img.naturalHeight : 1;
    return sw / sh > ratio ? { w: sh * ratio, h: sh } : { w: sw, h: sw / ratio };
  }

  /**
   * Zooms to `scale`, keeping the point (px, py) — relative to the stage centre —
   * under the pointer. Defaults to the centre.
   * @param {number} scale
   * @param {number} [px]
   * @param {number} [py]
   * @param {boolean} [animate]
   */
  #zoomTo(scale, px = 0, py = 0, animate = true) {
    const next = Math.min(ZoomDialog.MAX_SCALE, Math.max(1, scale));
    const ratio = next / this.#scale;
    this.#x = px - (px - this.#x) * ratio;
    this.#y = py - (py - this.#y) * ratio;
    this.#scale = next;
    if (next > 1) this.#loadFullResolution();
    this.#apply(animate);
  }

  /** @param {boolean} animate */
  #apply(animate) {
    const slide = this.#slides[this.#active];
    const img = /** @type {HTMLElement | null} */ (slide?.firstElementChild ?? null);
    if (!img) return;
    const { stage, dialog } = this.refs;
    const { w, h } = this.#baseSize();
    const maxX = Math.max(0, (w * this.#scale - stage.clientWidth) / 2);
    const maxY = Math.max(0, (h * this.#scale - stage.clientHeight) / 2);
    this.#x = Math.min(maxX, Math.max(-maxX, this.#x));
    this.#y = Math.min(maxY, Math.max(-maxY, this.#y));
    if (this.#scale === 1) this.#x = this.#y = 0;

    img.classList.toggle('is-animated', animate);
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      img.style.transform = `translate3d(${this.#x}px, ${this.#y}px, 0) scale(${this.#scale})`;
    });
    dialog.toggleAttribute('data-zoomed', this.#scale > 1);
    dialog.querySelector('[data-zoom-action="out"]')?.toggleAttribute('disabled', this.#scale <= 1);
    dialog.querySelector('[data-zoom-action="reset"]')?.toggleAttribute('disabled', this.#scale <= 1);
    dialog.querySelector('[data-zoom-action="in"]')?.toggleAttribute('disabled', this.#scale >= ZoomDialog.MAX_SCALE);
  }

  /** @param {PointerEvent} event */
  #point(event) {
    const rect = this.refs.stage.getBoundingClientRect();
    return { x: event.clientX - rect.left - rect.width / 2, y: event.clientY - rect.top - rect.height / 2 };
  }

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (event.button !== 0 && event.pointerType === 'mouse') return;
    try {
      this.refs.stage.setPointerCapture(event.pointerId);
    } catch {
      // Pointer already released (or not capturable): track it without capture.
    }
    this.#pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const points = Array.from(this.#pointers.values());
    this.#gesture = {
      x: points.length === 2 ? (points[0].x + points[1].x) / 2 : event.clientX,
      y: points.length === 2 ? (points[0].y + points[1].y) / 2 : event.clientY,
      tx: this.#x,
      ty: this.#y,
      dist: points.length === 2 ? Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y) : 0,
      scale: this.#scale,
      moved: points.length === 2,
    };
    this.refs.dialog.classList.add('is-gesturing');
  };

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    if (!this.#pointers.has(event.pointerId) || !this.#gesture) return;
    this.#pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const points = Array.from(this.#pointers.values());
    const g = this.#gesture;

    if (points.length === 2 && g.dist) {
      // Pinch: scale around the fingers' midpoint.
      const dist = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
      const rect = this.refs.stage.getBoundingClientRect();
      const mid = { x: (points[0].x + points[1].x) / 2 - rect.left - rect.width / 2, y: (points[0].y + points[1].y) / 2 - rect.top - rect.height / 2 };
      this.#x = g.tx;
      this.#y = g.ty;
      this.#scale = g.scale;
      this.#zoomTo(g.scale * (dist / g.dist), mid.x, mid.y, false);
      return;
    }

    const dx = event.clientX - g.x;
    const dy = event.clientY - g.y;
    if (Math.hypot(dx, dy) > 6) g.moved = true;
    if (this.#scale > 1) {
      this.#x = g.tx + dx;
      this.#y = g.ty + dy;
      this.#apply(false);
    } else if (event.pointerType !== 'mouse') {
      // Unzoomed touch: let the image follow the finger sideways as a swipe hint.
      const img = /** @type {HTMLElement | null} */ (this.#slides[this.#active]?.firstElementChild ?? null);
      if (img) img.style.transform = `translate3d(${dx * 0.6}px, 0, 0)`;
    }
  };

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    const g = this.#gesture;
    const wasPinch = this.#pointers.size === 2;
    this.#pointers.delete(event.pointerId);
    if (this.#pointers.size > 0) {
      // Pinch ended with one finger left: restart a pan from here.
      const [rest] = Array.from(this.#pointers.values());
      this.#gesture = { x: rest.x, y: rest.y, tx: this.#x, ty: this.#y, dist: 0, scale: this.#scale, moved: true };
      return;
    }
    this.refs.dialog.classList.remove('is-gesturing');
    this.#gesture = null;
    if (!g || wasPinch) return;

    const dx = event.clientX - g.x;
    const dy = event.clientY - g.y;

    if (g.moved) {
      if (this.#scale === 1 && event.pointerType !== 'mouse' && Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
        this.#go(this.#active + (dx < 0 ? 1 : -1));
      } else if (this.#scale === 1) {
        this.#apply(true);
      }
      return;
    }

    // A tap or click toggles zoom at that point (double-tap on touch).
    const point = this.#point(event);
    if (event.pointerType !== 'mouse') {
      const now = Date.now();
      if (now - this.#lastTap > 300) {
        this.#lastTap = now;
        return;
      }
      this.#lastTap = 0;
    }
    if (this.#scale > 1) this.#zoomTo(1);
    else this.#zoomTo(ZoomDialog.CLICK_SCALE, point.x, point.y);
  };

  /** @param {WheelEvent} event */
  #onWheel = (event) => {
    event.preventDefault();
    const point = this.#point(/** @type {PointerEvent} */ (/** @type {unknown} */ (event)));
    this.#zoomTo(this.#scale * Math.exp(-event.deltaY * 0.0015), point.x, point.y, false);
  };
}

/**
 * Returns a Shopify CDN image URL at the given width.
 * @param {string} url
 * @param {number} width
 */
function resize(url, width) {
  try {
    const parsed = new URL(url, window.location.href);
    parsed.searchParams.set('width', String(width));
    return parsed.toString();
  } catch {
    return url;
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}
